import React from 'react';
import WithBackground from 'components/WithBackground/WithBackground';
import { store } from 'stores/Store';
import useWindowSize from 'hooks/useWindowSize';
import { observer } from 'mobx-react';
import FloatingButton from 'components/Desktop/DesktopInput/FloatingButton/FloatingButton';
import PromptsBar from 'components/PromptsBar/PromptsBar';
import DeepgramS2T from 'components/Speech/DeepgramS2T/DeepgramS2T';
import cx from "classnames";
import styles from './Chatbot.module.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useExternalApiService from './../../helpers/ExternalApiService';
import Heatmap from 'components/Heatmap';
import ShowTyping from 'components/ShowTyping/ShowTyping';
import Button from 'components/Button/Button';
import { ReactComponent as StopIcon } from 'assets/stop.svg';
import { ReactComponent as ThumbsUpIcon } from 'assets/thumbs_up.svg';
import { ReactComponent as ThumbsDownIcon } from 'assets/thumbs_down.svg';
import { ReactComponent as ThumbsUpFilledIcon } from 'assets/thumbs_up_filled.svg';
import Feedback from 'Modules/Feedback';
import Text from "components/Text/Text"
import BaseballCard from 'Modules/PractitionerBaseballCard';
import { Tab } from 'Modules/PractitionerBaseballCard';
import { Tab as MapRouterTab } from 'Modules/MapRouter';
import MapRouter from 'Modules/MapRouter';
import InterruptController from 'components/InterruptController/InterruptController';

import OpportunityForm from 'Modules/Opportunities/OpportunityForm';
import CreateLead from 'Modules/CreateLead';

import { logEvent } from '@amplitude/analytics-browser'; // ← browser SDK

interface TableJson {
  table_header: string[];
  table_content: { [key: string]: string | number }[];
}

interface OllamaApiResponse {
  model: string;
  created_at: string;
  response: string;
  text: string;
  vid: string;
  pic: string;
  waiting: boolean;
  done: boolean;
  questions: string[];
  table_json: TableJson;
  render_table: boolean;
  render_heatmap: boolean;
  heatmap: { x: string; y: string, value: number | null }[];
  message_id: string;
  baseballcard_loc?: string;
  general_notes?: string;
  practitioner_list?: string[];
  checkin_notes?: string;
}

interface llmResponse {
  success: boolean;
  error_msg: string;
  payload: OllamaApiResponse;
  message_id: string;
}

interface ChatEntry {
  message: string;
  tableJson?: TableJson;
  messageId?: string;
}

const ChatBot: React.FC = observer(() => {
  // Move controller to a ref so it persists between renders
  const controllerRef = React.useRef<AbortController | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const prompt = searchParams.get("prompt");
  const toggle = searchParams.get("toggle");
  const _subTab = searchParams.get("targetSubTab");
  const [subTab, setSubTab] = React.useState("");
  const [isOnline, setIsOnline] = React.useState(navigator.onLine);
  const [stopTyping, setStopTyping] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isIframeVisible, setIsIframeVisible] = React.useState(false);
  const [micOn, setMicOn] = React.useState(false);
  const [waitingResponse, setwaitingResponse] = React.useState(false);  // Frontend waiting for backend's LLM response.  Show loading
  const [answers, setAnswers] = React.useState<ChatEntry[]>([]); // For single-turn multiple questions answering
  const [selectedView, setSelectedView] = React.useState<string | null>(null);
  const [rerender, setRerender] = React.useState(false);
  const [animationClass, setAnimationClass] = React.useState('');
  const [chatClass, setChatClass] = React.useState('');
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const [isTyping, setIsTyping] = React.useState(false);
  const [showScrollButton, setShowScrollButton] = React.useState(false);
  const [showFeedback, setShowFeedback] = React.useState(false);
  const [showTostr, setShowTostr] = React.useState(false);
  const [basebalCardTab, setBasebalCardTab] = React.useState<Tab>(Tab.SUMMARY);
  const [generalNotes, setGeneralNotes] = React.useState<string>('');
  const [mapRouterTab, setMapRouterTab] = React.useState<MapRouterTab>(MapRouterTab.CURRENT_ROUTE);
  const [selectedFeedbackIndex, setSelectedFeedbackIndex] = React.useState(0);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = React.useState(false);
  const [hasRouteChanged, setHasRouteChanged] = React.useState(false);
  // const [checkInQnAStep, setCheckInQnAStep] = React.useState<number | null>(null);
  const [isVoicePlaying, setIsVoicePlaying] = React.useState(false);
  const interruptControllerRef = React.useRef<any>(null);
  const [closeAttempted, setCloseAttempted] = React.useState(false);
  const isInitialRender = React.useRef(true);
  const isInitialRenderTg = React.useRef(true);
  const _extraArgs = searchParams.get("extraArgs");
  const [extraArgs, setExtraArgs] = React.useState<any>();


  // React.useEffect(() => {
  //   if (stopTyping && controllerRef.current) {
  //     controllerRef.current.abort();
  //     setIsTyping(false);
  //     setIsLoading(false); // Reset loading state when stopping
  //   }
  // }, [stopTyping]);

  React.useEffect(() => {
    setExtraArgs(_extraArgs);
  }, [_extraArgs]);

  // Cleanup effect to ensure modal state is reset on mount
  React.useEffect(() => {
    // Ensure modal is closed and no leftover state when component mounts
    setIsIframeVisible(false);
    setSelectedView(null);
    setAnimationClass('');
    setChatClass('');
  }, []);

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (!showFeedback) {
      setShowTostr(true);
      timeoutId = setTimeout(() => {
        setShowTostr(false);
      }, 500);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showFeedback]);

  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  React.useEffect(() => {
    if (prompt) {
      handleSubmit(prompt, true)
      setSearchParams({});
    }
  }, [prompt])

  React.useEffect(() => {
    setSubTab(_subTab || "");
  }, [_subTab]);

  const apiUrl = process.env.REACT_APP_EAI_URL;
  const { postData, getData } = useExternalApiService(apiUrl);

  // Amplitude: helper to track all button click submission to AI
    const trackClick = (buttonName: string) => {
      const label = buttonName
      logEvent(label, { buttonName });
    };

  /* Calling LLM from dev server */
  const getEaiUserName = async () => {
    const result = await postData('/api/get_user', {});
    // console.log("Finished calling postData at EAI endpoint", result);
    // console.log("user_name=", result.user_info.user_name);
    return result.user_info.user_name;
  };


  const sendMsgToLLMServiceEAI = async (
    msg: string,
    user_name: string,
    resume: boolean,
    answers_array: string[],
    signal?: AbortSignal
  ): Promise<OllamaApiResponse> => {
    try {
      const winback_prompt: string[] = [
        "Boo, this is a default answer"
      ];

      let response: llmResponse;

      if (winback_prompt.includes(msg)) {
        // console.log(`${msg} is in the array.`);

        response = await postData('/api/e/call_winback_agent_microservice',
          {
            'message': msg,
            'fmc_email': store.impersonatedUser,
            'resume': true,
            'answers_array': []
          },
          signal
        );
      } else {
        response = await postData('/api/e/call_winback_agent_microservice',
          {
            'message': msg,
            'fmc_email': store.impersonatedUser,
            'resume': true,
            'answers_array': []
          },
          signal
        );
      }

      if (response.success === true) {

        if(response.payload.response === "" && response.payload.baseballcard_loc === ""){
          store.addAnswer('Prompt not avaiable, please try another one.');
          interruptControllerRef.current?.handleInterrupt();
        }
        const result: OllamaApiResponse = { ...response.payload, message_id: response.message_id };
        console.log('Server response:', result);

        return result;
      } else {
        store.addAnswer('An error occurred while processing your request.  Please try again.');
        console.error('Server error.  Full response object is=', response);
        return {
          model: '',
          created_at: '',
          response: 'There is an issue pulling your data.  We will work with DFH IT to fix this.  In the meantime, can you try another prompt?  We appreciate your patience.',
          text: '',
          vid: 'nan',
          pic: 'nan',
          waiting: false,
          done: false,
          questions: [],
          table_json: {
            table_header: [],
            table_content: [{}],
          },
          render_table: false,
          render_heatmap: false,
          heatmap: [],
          message_id: ''
        };
      }

    } catch (error: any) {
      console.error('Network error:', error);
      return {
        model: '',
        created_at: '',
        response: error.name !== 'AbortError' ? "Your internet connection is spotty, please try again soon..." : "",
        text: '',
        vid: '',
        pic: '',
        waiting: false,
        done: false,
        questions: [],
        table_json: {
          table_header: [],
          table_content: [{}],
        },
        render_table: false,
        render_heatmap: false,
        heatmap: [],
        message_id: ''
      };
    }
  }
  ///////// End LLM service /////


  const { width, height } = useWindowSize();

  // Focus textarea when isTyping changes to false
  React.useEffect(() => {
    if (!isTyping && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isTyping]);

  React.useEffect(() => {
    if (closeAttempted && !hasRouteChanged) {
      closeIframe();
    }
    if(!showUnsavedChangesModal) {
      setCloseAttempted(false);
    }
  }, [showUnsavedChangesModal]);

  const navigate = useNavigate();
  const handleToggleIframe = (type: string = 'iframe', tabName: any = undefined) => {
    setSelectedView(type);
    if (!isIframeVisible) {
      setIsIframeVisible(true);
    }
    // if(!(toggle === 'false')) {
    //   setIsIframeVisible(!isIframeVisible);
    // }
  };

  const renderCustomView = () => {
    switch (selectedView) {
      case 'iframe':
        return (
          <iframe
            title='eisengard-view'
            style={{ width: '100%', height: '100%', border: 'none' }}
            allowFullScreen={true}
            src="https://app.eisengard.ai/shared_canvas_module.html?remote_moduleid=Fy5nPC5cRpeLmr8SIp%2F%2FeA">
          </iframe>
        )
      case 'heatmap':
        return (
          <Heatmap width={width > 480 ? width * .6 : width * .9} height={height * .8} data={store.heatmap} />
        )
      case 'baseballcard':
        return (
          <div style={{ width: width > 480 ? width * .6 : width * .9, height: height * .8, }}>
            <BaseballCard tab={basebalCardTab} generalNotes={generalNotes} subTab={subTab||""} />
          </div>
        )
      case 'createRoute':
        return (
          <div style={{width: width > 480 ? width * .6 : width * .9, height: height * .8, position: 'relative'}}> 
            <MapRouter setShowUnsavedChangesModal={setShowUnsavedChangesModal} showUnsavedChangesModal={showUnsavedChangesModal}
                       tab={mapRouterTab} setSelectedView={setSelectedView} hasRouteChanged={hasRouteChanged} setHasRouteChanged={setHasRouteChanged}/>
          </div>
        )
      case 'newLead':
        return (
          <div style={{width: "100%", height: height * .8, position: 'relative'}}> 
            <CreateLead initialSearchValue="" extraArgs={extraArgs} />
          </div>
        )
      default:
        if (selectedView?.includes("opportunity") || selectedView?.includes("risk")) {
          const [target ,action] = selectedView.split(":");
          if (action === "list") {
            return (<div style={{ width: width > 480 ? width * .6 : width * .9, height: height * .8, position: 'relative' }}>
              <BaseballCard tab={Tab.OPPORTUNITIES} generalNotes={generalNotes} />
            </div>)
          }
          return (
            <>
              <div 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                style={{
                  backgroundColor: "white",
                  position: "absolute",
                  zIndex: 15,
                  top: 0,
                  right: 0,
                  width: 60,
                  height: 75,
                }}>
              </div>
              <div style={{ width: width > 480 ? width * .6 : width * .95, height: height * .8, position: 'relative', overflowY: "scroll", marginTop: "-2px" }}>
                <OpportunityForm action={action} target={target} />
              </div>
            </>
          )
        }
        return null;
    }
  }

  const handleCloseRoute = (hasRouteChanged: boolean) => {
    setCloseAttempted(true);
    if(hasRouteChanged) {
      setShowUnsavedChangesModal(true);
    }else{
      setIsIframeVisible(false);
    }
  }

  const closeIframe = () => {
    setIsIframeVisible(false);
  };

  const navigateToMap = () => {
    navigate(`/mapView`);
  }

  React.useEffect(() => {
    // When the input changes, set the animation class to slide in or out based on whether the input is empty or not
    if (isIframeVisible) {
      // setSelectedView('iframe')
      setTimeout(() => {
        setChatClass('move-left');
        setAnimationClass('slide-in');
      }, 50);
    } else {
      setAnimationClass('slide-out');
      setChatClass('');
      // Only clear selectedView after the slide-out animation completes
      const timeoutId = setTimeout(() => {
        setSelectedView(null)
      }, 1000); // Match the CSS transition duration
      
      // Cleanup timeout if component unmounts
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [isIframeVisible]);

  React.useEffect(() => {
    handleScrollToBottom();
  }, [store.userInput, store.answers, store.prompts, showFeedback]);

  React.useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isTyping) {
      intervalId = setInterval(() => {
        handleScrollToBottom();
      }, 500);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isTyping]);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement> | string, asString: boolean = false) => {
    store.setUserInput(asString ? event : (event as any).target.value);
  };

  const handleScrollToBottom = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: scrollContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }

  // Preprocess the markdown to replace [button:...] with <Button text="..."/>
  const preprocessMarkdownToButton = (markdown: string) => {
    return markdown.replace(/\[button:(.*?)\]/g, '[$1](#button:$1)');
  };

  const handleInterrupt = () => {
    store.addAnswer(' ', true);
    interruptControllerRef.current?.handleInterrupt();
  };

  const handleNewQuestion = async (userInput: string) => {
    setStopTyping(false);
    // Create new controller for each request
    controllerRef.current = new AbortController();

    /*if (userInput === "I'm going to Albany next week, who should I talk to?" || userInput === "asd") {
      const bakedMarkdwon = preprocessMarkdownToButton(`Of course, you had discussed with your director to use the Acquisition-Retention-Expansion (ARE) framework to prioritize, these are the best 5 people you should talk to: \n  
      `)
      store.addAnswer(bakedMarkdwon);
      store.setRenderCustom(true);
    } else */
    if (userInput.trim() === "show a dashboard") {
      handleToggleIframe('iframe')
      store.setRenderCustom(false);
      store.addAnswer(' ');
      setIsTyping(false);
      return;
    } else if (userInput.trim().toLocaleLowerCase() === "danville, ca") {
      navigate(`/mapView?location=danville,ca`)
      setIsTyping(false);
      return;
    } else if (userInput.toLocaleLowerCase().trim() === "show my territory" && store.user?.earlyRelease) {
      navigateToMap()
    } else if (store.user?.isAdmin && store.impersonatedUser === store.user?.userName) {
      store.addPrompt(userInput);
      store.addAnswer({ message: "The ability to query as an admin is coming soon", messageId: '' });
      setIsTyping(false);
    } else if (userInput.toLocaleLowerCase().trim() === "pull up general notes for pamela practitioner" && store.user?.earlyRelease) {
      store.setCheckIn({ practitioner_id: 19 });
      setBasebalCardTab(Tab.NOTES);
      handleToggleIframe('baseballcard');
      setIsTyping(false);
    } else if (userInput.toLocaleLowerCase().trim() === "pull up check-in history for pamela practitioner" && store.user?.earlyRelease) {
      store.setCheckIn({ practitioner_id: 19 });
      setBasebalCardTab(Tab.CHECK_IN);
      handleToggleIframe('baseballcard');
      setIsTyping(false);
    } else if (userInput.toLocaleLowerCase().trim() === "create a new route" && store.user?.earlyRelease) {
      handleToggleIframe('createRoute');
      setIsTyping(false);
      return;
    }
    if (userInput.toLocaleLowerCase().trim().includes("risk") && store.user?.earlyRelease) {
      setSelectedView(userInput.trim());
      setIsTyping(false);
      return;
    } else if (userInput.toLocaleLowerCase().trim().includes("opportunity") && store.user?.earlyRelease) {
      setSelectedView(userInput.trim());
      setIsTyping(false);
      return;
    } else if (userInput.toLocaleLowerCase().trim() === "practitioners" && store.user?.earlyRelease) {
      setBasebalCardTab(Tab.PRACTITIONERS_LIST);
      handleToggleIframe('baseballcard');
      setIsTyping(false);
      return;
    } else if (userInput.toLocaleLowerCase().trim() === "show baseball card for pamela practitioner" && store.user?.earlyRelease) {
      store.setCheckIn({ notes: userInput, practitioner_id: 19 });
      setBasebalCardTab(Tab.SUMMARY);
      handleToggleIframe('baseballcard');
      setIsTyping(false);
      return;
    } else if (userInput.toLocaleLowerCase().trim() === "help me create a new lead") {
      handleToggleIframe("newLead");
      setIsTyping(false);
      return ;
    } else {
      store.setRenderCustom(false);
      store.addPrompt(userInput);

      // Old version of LLM service from turtle_api:demobot
      //const ollamaResponse = await store.sendMsgToLLMService(userInput, store.userName, store.waitingForAnswers, []);

      // This uses the dev's microservice
      const ollamaResponse = await sendMsgToLLMServiceEAI(
        userInput,
        store.user?.userName || '',
        false,
        [],
        controllerRef.current.signal  // Pass the signal
      );

      // For some reason the text response from ollamaResponse is escaping \n's by adding an extra "\".  Let's properly escape it for rednering in markdown.
      if (ollamaResponse.text !== undefined) {
        let mkdwnText = ollamaResponse.text.replace(/\\n/g, '\n');
        // console.log("msg=", ollamaResponse.response);
        store.setText(mkdwnText);
        // console.log("txt=", mkdwnText);
        store.setPic(ollamaResponse.pic);
        store.setVid(ollamaResponse.vid);
      }



      store.setRenderCustom(ollamaResponse.render_table);

      if (ollamaResponse.render_heatmap) {
        handleToggleIframe('heatmap');
        store.setHeatmap(ollamaResponse.heatmap);
        setIsTyping(false);
      }

      // baseball card rendereing piece
      if (ollamaResponse.baseballcard_loc !== "" && store.user?.earlyRelease) {

        //The practitioner lookup is done by the practitioner_id on the store.checkIn object.
        store.setCheckIn({ practitioner_name: ollamaResponse.practitioner_list?.[0] });
        switch (ollamaResponse.baseballcard_loc) {
          case "summary":

            if (!ollamaResponse.practitioner_list?.length) {
              store.addAnswer({ message: "I could not find a practitioner by that name. Please check for typos, or try a different way to spell the name.", messageId: '' });
              setIsTyping(false);
              store.setWaitingForAnswers(false);
              return;
            } else {
              store.addAnswer({
                message: ollamaResponse.response ?? "Practitioner list opened.",
                tableJson: ollamaResponse.table_json,
                messageId: ollamaResponse.message_id
              });
            }
            handleToggleIframe('baseballcard');
            setBasebalCardTab(Tab.SUMMARY);
            setIsTyping(false);
            break;
          case "details":
            if (!ollamaResponse.practitioner_list?.length) {
              store.addAnswer({ message: "I could not find a practitioner by that name. Please check for typos, or try a different way to spell the name.", messageId: '' });
              setIsTyping(false);
              store.setWaitingForAnswers(false);
              return;
            } else {
              store.addAnswer({
                message: ollamaResponse.response ?? "Practitioner details opened.",
                tableJson: ollamaResponse.table_json,
                messageId: ollamaResponse.message_id
              });
            }
            handleToggleIframe('baseballcard');
            setBasebalCardTab(Tab.DETAILS);
            setIsTyping(false);
            break;
          case "checkin_view":
            if (!ollamaResponse.practitioner_list?.length) {
              store.addAnswer({ message: "I could not find a practitioner by that name. Please check for typos, or try a different way to spell the name.", messageId: '' });
              setIsTyping(false);
              store.setWaitingForAnswers(false);
              return;
            } else {
              store.addAnswer({
                message: ollamaResponse.response ?? "Check-in history opened.",
                tableJson: ollamaResponse.table_json,
                messageId: ollamaResponse.message_id
              });
            }
            handleToggleIframe('baseballcard');
            setBasebalCardTab(Tab.CHECK_IN);
            setIsTyping(false);
            break;
          case "checkin_write":

            if (ollamaResponse.checkin_notes) {
              handleToggleIframe('baseballcard');
              setBasebalCardTab(Tab.CHECK_IN);
              setIsTyping(false);
              store.setCheckIn({ notes: ollamaResponse.checkin_notes });
            }

            store.addAnswer({
              message: ollamaResponse.response ?? "Check-in history opened.",
              tableJson: ollamaResponse.table_json,
              messageId: ollamaResponse.message_id
            });

            // "Check-in for [practitioner]"
            // TOOD: Pedro: this is the same as your hard-coded version for check-in at pamela practitioner.  Please add code and make sure practitioner look-up works.
            break;
          case "notes_view":
            // Pull up notes for [practitioner]
            // TODO: 
            //   1. Pedro, we need to handle an exception here where if there is no [practitioner] is returned or if none if matched, we need to return a message saying: "I could not find a practitioner by that name, here is the [full list of practitioners](open up baseball card with list of practitioners)"

            if (!ollamaResponse.practitioner_list?.length) {
              store.addAnswer({ message: "I could not find a practitioner by that name. Please check for typos, or try a different way to spell the name.", messageId: '' });
              setIsTyping(false);
              store.setWaitingForAnswers(false);
              return;
            } else {
              store.addAnswer({
                message: ollamaResponse.response ?? "Notes opened.",
                tableJson: ollamaResponse.table_json,
                messageId: ollamaResponse.message_id
              });
            }
            handleToggleIframe('baseballcard');
            setBasebalCardTab(Tab.NOTES);
            setIsTyping(false);
            break;
          case "notes_write":
            // Create a new note for [practitioner]
            // TODO: Pedro: you need to open up the Tab.NOTES section.  Then append the notes from the prompt and add it here.  

            if (ollamaResponse.general_notes) {
              handleToggleIframe('baseballcard');
              setBasebalCardTab(Tab.NOTES);
              setGeneralNotes(ollamaResponse.general_notes);
              setIsTyping(false);
            }

            if (!ollamaResponse.practitioner_list?.length) {
              store.addAnswer({ message: "I could not find a practitioner by that name. Please check for typos, or try a different way to spell the name.", messageId: '' });
              setIsTyping(false);
              store.setWaitingForAnswers(false);
              return;
            } else {
              store.addAnswer({
                message: ollamaResponse.response=="" ? "Notes opened." : ollamaResponse.response,
                tableJson: ollamaResponse.table_json,
                messageId: ollamaResponse.message_id
              });
            }

            break;
          case "routes_saved":
            // Pull up my routes
            // TODO: Pedro: for this you need to open up the "saved routes page (see the spreadsheet for the exact screen and location)

            store.addAnswer({
              message: ollamaResponse.response=="" ? "Saved routes opened." : ollamaResponse.response,
              tableJson: ollamaResponse.table_json,
              messageId: ollamaResponse.message_id
            });
            setMapRouterTab(MapRouterTab.SAVED_ROUTES);
            handleToggleIframe('createRoute');
            setIsTyping(false);
            break;
          case "routes_map":
            // Show my territory
            // DONE
            navigateToMap();
            break;
          case "route_current":
            // Same as "Create a new route"
            // DONE.  
            handleToggleIframe('createRoute');
            setIsTyping(false);
            store.addAnswer({
              message: ollamaResponse.response=="" ? "Routes creation opened." : ollamaResponse.response,
              tableJson: ollamaResponse.table_json,
              messageId: ollamaResponse.message_id
            });

            break;
          case "opportunities":
            handleToggleIframe('baseballcard');
            setBasebalCardTab(Tab.OPPORTUNITIES);
            setIsTyping(false);
            store.addAnswer({
              message: ollamaResponse.response=="" ? "Opportunities opened." : ollamaResponse.response,
              tableJson: ollamaResponse.table_json,
              messageId: ollamaResponse.message_id
            });

            break;
          case "stats":
            handleToggleIframe('baseballcard');
            setBasebalCardTab(Tab.STATS);
            setIsTyping(false);
            store.addAnswer({
              message: ollamaResponse.response=="" ? "Stats opened." : ollamaResponse.response,
              tableJson: ollamaResponse.table_json,
              messageId: ollamaResponse.message_id
            });

            break;
          case "all_practitioners":
            // Show all accounts
            // DONE. 
            handleToggleIframe('baseballcard');
            setBasebalCardTab(Tab.PRACTITIONERS_LIST);
            setIsTyping(false);
            store.addAnswer({
              message: ollamaResponse.response=="" ? "Practitioners list opened." : ollamaResponse.response,
              tableJson: ollamaResponse.table_json,
              messageId: ollamaResponse.message_id
            });
            break;
        }
      } else {
        store.addAnswer({
          message: ollamaResponse.response=="" ? "I'm sorry, I can't answer that question." : ollamaResponse.response,
          tableJson: ollamaResponse.table_json,
          messageId: ollamaResponse.message_id
        });
      }

      store.setWaitingForAnswers(ollamaResponse.waiting);
      if (ollamaResponse.waiting) {
        store.setMyQuestions(ollamaResponse.questions);  // Assuming backend sends an array of questions                
        store.setWaitingForAnswers(true);  // This might be redundant since at this point waitingForAnswers is already true.                  
        await new Promise(r => setTimeout(r, 3500)) // just wait for 3.5 s to simulate processing for now.
        store.addAnswer(store.myQuestions[0]); // Send the first question.
        store.setCurrentQuestionIndex(store.currentQuestionIndex + 1);
      }
    }

  };

  const handleNextQuestion = async (userInput: string) => {
    setStopTyping(false);
    const newAnswers = [...answers, { message: userInput }];
    setAnswers(newAnswers);

    if (store.currentQuestionIndex < store.myQuestions.length) {
      store.addPrompt(userInput);
      store.addAnswer(store.myQuestions[store.currentQuestionIndex]);
      store.setCurrentQuestionIndex(store.currentQuestionIndex + 1);
    } else {
      store.addPrompt(userInput);

      const lastResponse = await sendMsgToLLMServiceEAI(
        "These are my answers",
        store.user?.userName || '',
        store.waitingForAnswers,
        newAnswers.map(a => a.message),
        controllerRef.current?.signal
      );

      store.addAnswer({ message: lastResponse.response, messageId: lastResponse.message_id });  // Sends th final response back.  
      // Reset all relevant states so we start afresh
      store.setCurrentQuestionIndex(0); // Reset the index of questions.
      store.setWaitingForAnswers(false);

      setAnswers([]); // Reset the list of answers to be sent to the back.  
    }
  };

  const handleSubmit = async (value: React.KeyboardEvent<HTMLTextAreaElement> | string, asString: boolean = false) => {
    handleScrollToBottom();
    if (!asString) {
      if ((value as any).key === 'Enter' && store.userInput.trim()) {
        (value as any).preventDefault();
        setIsTyping(true);
        setIsLoading(true);
        const userInput = store.userInput;

        /* Clear textarea field after submitting  */
        store.setUserInput(''); // Clears it via the MobX store.

        if (store.waitingForAnswers) {
          // console.log("I'm waiting for answers");
          await handleNextQuestion(userInput);
          // console.log("Finished waiting for waitingForAnswers condition.  WaitingforAnswers (Should be false when done with all questions)=", store.waitingForAnswers);
        } else {
          await handleNewQuestion(userInput);
        }

        /* Clear textarea field after submitting process is done  */
        store.setUserInput(''); // Clears it via the MobX store.                  
        setIsLoading(false);
      }
    } else {
      setwaitingResponse(true);
      setIsLoading(true);
      setIsTyping(true);
      const userInput = value as string;
      store.setUserInput(''); // Clear input textArea after submitting      

      if (store.waitingForAnswers) {
        await handleNextQuestion(userInput);
      } else {
        await handleNewQuestion(userInput);
      }

      /* Clear textarea field after submitting process is done  */
      store.setUserInput(""); // Clears it via the MobX store.
      setwaitingResponse(false);
      setIsLoading(false);
      trackClick('Submitted Question to AI');
    }
  };

  const toggleMic = () => { 
    setMicOn(!micOn) 
    trackClick('Toggled Mic');
  }
  const customComponent = (table_json: TableJson) => {
    return (
      <table>
        <thead>
          <tr>
            {table_json.table_header.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table_json.table_content.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {table_json.table_header.map((header, colIndex) => (
                <td key={colIndex}>
                  {header === "Strategy" ? (
                    <a
                      style={{ cursor: 'pointer' }}
                      className={`customButton${row[colIndex]}`}
                      onClick={() => handleToggleIframe()}
                    >
                      {row[colIndex]}
                    </a>
                  ) : header === "Success" ? (
                    <a className="customButtonProb">
                      Prob: {Number(row[colIndex]) * 100}%
                    </a>
                  ) : header === "CLV" ? (
                    `$${Number(row[colIndex]).toLocaleString()}`
                  ) : (
                    row[colIndex]
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }


  React.useEffect(() => {
    // load  product list only once
    getData('/api/get_products').then((data) => {
      store.setAvailableProducts(data.products as any);
    });

    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollHeight, clientHeight, scrollTop } = scrollContainerRef.current;
        const shouldShow = scrollHeight > clientHeight + scrollTop + 100;
        setShowScrollButton(shouldShow);
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      // Initial check
      handleScroll();
    }

    // Re-run effect when div content changes
    const observer = new MutationObserver(handleScroll);
    if (scrollContainer) {
      observer.observe(scrollContainer, {
        childList: true,
        subtree: true
      });
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      observer.disconnect();
    };
  }, []);


  // if negative rating, get the opts
  const handleFeedback = async (rating: string, feedback: string, index: number) => {
    setShowFeedback(false)
    const result = await postData('/api/add_message_feedback', {
      message_id: store.answers[index]?.messageId,
      feedback: feedback,
      rating: rating
    });
  }

  const thumbsHandler = (index: number, length: number) => {

    switch (store.promptsFeedback[index]) {
      case 'thumbsUp':
        return (
          <div className={`${styles.feedback} ${index === length - 1 ? styles.feedback__last : ''}`}>
            <ThumbsUpFilledIcon
              fill="#E95540"
              style={{ display: 'flex' }}
              onClick={() => { }}
            />
          </div>
        );
      case 'thumbsDown':
        return (
          <div className={`${styles.feedback} ${index === length - 1 ? styles.feedback__last : ''}`}>
            <ThumbsUpFilledIcon
              style={{ transform: 'rotate(180deg)', display: 'flex' }}
              fill="#E95540"
            />
          </div>
        );

      default:
        return (
          <div className={`${styles.P} ${index === length - 1 ? styles.feedback__last : ''}`}>
            <ThumbsUpIcon
              fill="#E95540"
              onClick={() => {
                if (!store.promptsFeedback[index]) {
                  handleFeedback('thumbsUp', '', index);
                  store.addPromptFeedback('thumbsUp', index);
                }
              }}
            />
            <ThumbsDownIcon
              fill="#E95540"
              onClick={() => {
                if (!store.promptsFeedback[index]) {
                  setShowFeedback(true);
                  setSelectedFeedbackIndex(index);
                  store.addPromptFeedback('thumbsDown', index);
                }
              }}
            />
          </div>
        )
    }
  }

  return (
    <WithBackground>
      <InterruptController
        ref={interruptControllerRef}
        setIsTyping={setIsTyping}
        setIsLoading={setIsLoading}
        setIsVoicePlaying={setIsVoicePlaying}
        controllerRef={controllerRef}
      />
      <div className={styles.app}>
        <div className={cx(styles.chatContainer, styles[chatClass])}>

          <div
            className={styles.scrollContainer}
            ref={scrollContainerRef}
            onScroll={React.useCallback(
              (() => {
                let lastCall = 0;
                return () => {
                  const now = Date.now();
                  if (now - lastCall >= 300) {
                    lastCall = now;
                    setRerender(!rerender);
                  }
                };
              })(),
              [rerender]
            )}
          >
            <div className={styles.scrollContent}>
              {store.prompts.map((prompt, index, array) => {

                // Calculate element position relative to viewport
                const elementEl = document.querySelectorAll('#question-container')[index];
                const elementRect = elementEl?.getBoundingClientRect();
                const elementTop = elementRect?.top || 0;
                const elementBottom = elementRect?.bottom || 0;

                // Calculate distance from center of viewport
                const viewportCenter = window.innerHeight / 2;
                const viewportTop = viewportCenter + 100; // Center zone top
                const viewportBottom = window.innerHeight;

                let scale = 1;

                // Scale down if element is outside center zone
                if (elementTop > viewportBottom || elementBottom < viewportTop) {
                  const distanceFromCenter = Math.min(
                    Math.abs(elementTop - viewportCenter),
                    Math.abs(elementBottom - viewportCenter)
                  );
                  const maxDistance = window.innerHeight / 2;
                  scale = 1 - (0.8 * Math.min(distanceFromCenter / maxDistance, 1));
                  scale = Math.max(scale, 0.3); // Minimum scale
                }

                // Force scale to 1 for the last element
                if (index === array.length - 1) {
                  scale = 1;
                }

                // Match opacity to scale but keep it more visible
                const opacity = (scale > 0.9) ? 1 : scale / 2;

                return (
                  <div
                    key={index}
                    id='question-container'
                    style={{
                      transform: `scale(${scale})`,
                      opacity: opacity,
                      transition: 'transform 0.3s, opacity 0.3s',
                      width: '100%'
                    }}
                  >
                    <div
                      className={`${styles.prompt} ${index > 0 ? styles.prompt__background : ''}`}
                    >
                      {prompt}
                    </div>
                    {store.answers[index]?.message && (
                      <div
                        className={`${styles.glass} ${index !== array.length - 1 ? styles.glass__previous : ''}`}
                      >
                        <ShowTyping
                          resumeTyping={index === array.length - 1 ? stopTyping : true}
                          setIsTyping={setIsTyping}
                          msg={store.answers[index]?.message}
                          txt={store.textDisplay}
                          pic={store.picURL}
                          vid={store.vidURL}
                          custom={store.answers[index]?.tableJson ?
                            () => customComponent(store.answers[index]?.tableJson!) :
                            undefined}
                          renderCustom={store.answers[index]?.tableJson ? true : false}
                          loading={index === array.length - 1 ? isLoading : false}
                        />
                        {index > 0 && store.answers[index]?.messageId && thumbsHandler(index, array.length)}
                      </div>
                    )}
                  </div>
                );
              })}
              {showFeedback && <Feedback index={selectedFeedbackIndex} callback={handleFeedback} onClose={() => setShowFeedback(false)} />}
              <div className={styles.tostr} style={{ opacity: showTostr ? 1 : 0 }}>
                <Text>Thansk for your feedback!</Text>
              </div>
            </div>
            {showScrollButton && !isLoading && (
              <button
                className={styles.scrollDownButton}
                onClick={() => {
                  handleScrollToBottom();
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 5v14M19 12l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
          {store.prompts.length === 1 && !isTyping && isOnline && (
            <div className={styles.buttonRow}>
              <Button type="squareButton" onClick={() => handleSubmit("What are my top business priorities?", true)}>
                <img src='/HowPic.jpg' style={{ borderTopLeftRadius: '10px', borderTopRightRadius: '10px', padding: 0, margin: 0, width: '100%' }} />
                <p>What are top business priorities?</p>
              </Button>
              <Button type="squareButton" onClick={() => handleSubmit("Who should I talk to?", true)}>
                <img src='/WhoPic.jpg' style={{ borderTopLeftRadius: '10px', borderTopRightRadius: '10px', padding: 0, margin: 0, width: '100%' }} />
                <p>Who should I talk to?</p>
              </Button>
              <Button type="squareButton" onClick={() => handleSubmit("What should I sell?", true)}>
                <img src='/WhatPic.jpg' style={{ borderTopLeftRadius: '10px', borderTopRightRadius: '10px', padding: 0, margin: 0, width: '100%' }} />
                <p>What should I sell?</p>
              </Button>
            </div>
          )}
          {(width > 600) &&
            <>
              <div
                style={{
                  width: '100%',
                  display: width >= 600 && store.prompts.length > 1 && !isTyping && !isLoading ? 'block' : 'none'
                }}
              >
                <PromptsBar
                  onOptSelected={(value: string) => {
                    handleSubmit(value, true)
                  }}
                />
              </div>
              {(!micOn) && (!isTyping) && !isLoading &&
                <>
                  {isOnline ? (
                    <textarea
                      {...({ enterKeyHint: 'send' } as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
                      ref={textareaRef}
                      className={styles.slickPrompt}
                      placeholder="Type message"
                      value={store.userInput}
                      onChange={handleInputChange}
                      onKeyDown={handleSubmit}
                      autoFocus
                    />
                  ) : (
                    <div className={styles.offlineMessage}>
                      <b>No Internet connection. </b> You'll be able to ask the AI as soon as you're back online.
                    </div>
                  )}
                </>
              }
            </>
          }
          {/* Stop button  */}
          {(micOn && isOnline) &&
            <DeepgramS2T
              isVoicePlaying={isVoicePlaying}
              setIsVoicePlaying={setIsVoicePlaying}
              setLoading={() => setIsLoading(true)}
              waitingResponse={waitingResponse}
              hidden={isTyping || isLoading}
              handleSubmit={(value: string) => { handleSubmit(value, true) }}
              interruptControllerRef={interruptControllerRef}
            />
          }
        </div>
        {selectedView && (
          <div
            className={cx(styles.iframeContainer, styles[animationClass])}
            onTouchStart={(e) => {
              const touch = e.touches[0];
              const startY = touch.clientY;

              // const handleTouchMove = (e: TouchEvent) => {
              //   const currentY = e.touches[0].clientY;
              //   if (currentY - startY > 50) { // If dragged down more than 50px
              //     closeIframe();
              //     document.removeEventListener('touchmove', handleTouchMove);
              //   }
              // };
              // document.addEventListener('touchmove', handleTouchMove);
              // document.addEventListener('touchend', () => {
              //   document.removeEventListener('touchmove', handleTouchMove);
              // }, { once: true });
            }}
          >            
              {selectedView === "newLead" ? (
                <div 
                  onClick={closeIframe}
                  className={styles["cancel-button"]}>CANCEL</div>
              ) : (
                <button className={styles.closeButton} onClick={closeIframe}>x</button>
              )}
              
            
            {/* {width < 600 && (
              <div style={{
                position: 'absolute', 
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '30vw',
                height: '3px',
                backgroundColor: '#808080',
                margin: '8px auto',
              }} />
            )} */}
            {renderCustomView()}
          </div>
        )}
        <div className={styles.footer} style={{ display: (!isTyping && !isLoading) ? 'block' : 'none' }}>
          {
            (width < 600) && (store.prompts.length > 1) && (
              <PromptsBar
                onOptSelected={(value: string) => { handleSubmit(value, true) }}
              />
            )
          }
          <div className={styles.footerInput} style={{ justifyContent: isOnline ? 'right' : 'center' }}>
            {(width < 600) && (!micOn) &&
              <>
                {isOnline ? (
                  <textarea
                    {...({ enterKeyHint: 'send' } as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
                    ref={textareaRef}
                    className={styles.slickPromptMobile}
                    placeholder="Type message"
                    value={store.userInput}
                    onChange={handleInputChange}
                    onKeyDown={handleSubmit}
                    autoFocus
                  />
                ) : (
                  <div className={styles.offlineMessage}>
                    Please be on internet in order to send messages.
                  </div>
                )}
              </>
            }
            {isOnline && (
              <FloatingButton
                hasPrompt={(store.userInput !== '')}
                micOn={micOn}
                toggle={toggleMic}
                waitingResponse={waitingResponse}
                onSubmit={() => handleSubmit(store.userInput, true)}
              />
            )}
          </div>
        </div>
        {(isTyping || isLoading || isVoicePlaying) &&
          <div
            onClick={handleInterrupt}
            className={styles.stopButton}>
            <StopIcon width={44} height={44} />
          </div>
        }
      </div>
    </WithBackground>
  );
});


export default ChatBot;
